import axios from 'axios';
import { getAuthHeaders } from '../utils/authUtils';
import localVideoUploadServiceInstance from './LocalVideoUploadService';

/**
 * Service for the "New Video (Split then Process)" feature.
 * Uploads the video (local or GDrive), then calls the backend splitAndProcess endpoint.
 */
class SplitVideoService {
    /**
     * Orchestrates the split-and-process flow.
     * Immediately returns a jobId (HTTP 202) and polls the status endpoint until completion.
     *
     * @param {Object}   params            - Same fields as before
     * @param {Function} [onStatusUpdate]  - Called with a human-readable progress string on each poll
     * @returns {Promise<Array>} Resolves with the created video parts array on success
     */
    async splitAndProcess(params, onStatusUpdate) {
        const {
            videoName,
            numberOfParts,
            linkType,
            selectedFile,
            googleLinkURL,
            folderPath,
            createLanguages,
            isCompressed,
            isLogging,
            bitRate,
            crfValue,
            subtitleMode,
            srtContent,
            srtFileName,
            srtLanguage,
            englishSrtContent,
            englishSrtFileName,
        } = params;

        const headers = getAuthHeaders(true);
        const apiBaseUrl = process.env.REACT_APP_API_URL;

        // Step 1: For local files, upload to server first to get stored filename
        if (onStatusUpdate) onStatusUpdate('Uploading video to server…');
        let videoUrl = '';
        if (linkType === 'local') {
            const uploadResponse = await localVideoUploadServiceInstance.uploadFile(selectedFile);
            videoUrl = uploadResponse?.data;
        } else {
            videoUrl = googleLinkURL;
        }

        // Step 2: Build query params
        const params2 = new URLSearchParams();
        params2.append('numberOfParts', String(numberOfParts));
        params2.append('folder', folderPath || '/');
        if (createLanguages) params2.append('createLanguages', createLanguages);
        params2.append('compressed', isCompressed ? 'true' : 'false');
        params2.append('detailedLogging', isLogging ? 'true' : 'false');
        params2.append('link', linkType);
        if (bitRate) params2.append('bitRate', bitRate);
        if (crfValue) params2.append('crf', crfValue);
        params2.append('subtitleMode', subtitleMode);
        if (srtLanguage) params2.append('srtLanguage', srtLanguage);

        // Step 3: Build request body
        const requestBody = { videoUrl, videoName };
        if (subtitleMode === 'srt' && srtContent) {
            requestBody.subtitleContent = srtContent;
            requestBody.subtitleFileName = srtFileName || 'subtitles.srt';
        }
        if (subtitleMode === 'srt' && englishSrtContent) {
            requestBody.englishSubtitleContent = englishSrtContent;
            requestBody.englishSubtitleFileName = englishSrtFileName || 'subtitles_english.srt';
        }

        // Step 4: POST — returns immediately with { jobId } (HTTP 202)
        if (onStatusUpdate) onStatusUpdate('Starting split job on server…');
        const endpoint = `${apiBaseUrl}/api/v3/videos/splitAndProcess?${params2.toString()}`;
        const response = await axios.post(endpoint, requestBody, { headers });
        const { jobId } = response.data;
        if (!jobId) throw new Error('Server did not return a job ID.');

        // Step 5: Poll status every 5 seconds until COMPLETED or FAILED
        return this._pollJobStatus(jobId, apiBaseUrl, headers, onStatusUpdate);
    }

    async _pollJobStatus(jobId, apiBaseUrl, headers, onStatusUpdate) {
        const POLL_INTERVAL_MS = 5000;
        const MAX_POLLS = 360; // 30 minutes max

        for (let attempt = 0; attempt < MAX_POLLS; attempt++) {
            await new Promise((resolve) => setTimeout(resolve, POLL_INTERVAL_MS));

            const statusRes = await axios.get(
                `${apiBaseUrl}/api/v3/videos/splitAndProcess/status/${jobId}`,
                { headers },
            );
            const { status, message, videos, error } = statusRes.data;

            if (onStatusUpdate && message) onStatusUpdate(message);

            if (status === 'COMPLETED') return videos || [];
            if (status === 'FAILED') throw new Error(error || 'Split and Process failed on the server.');
        }
        throw new Error('Split and Process timed out after 30 minutes.');
    }
}

const splitVideoServiceInstance = new SplitVideoService();
export default splitVideoServiceInstance;
